import { useState } from 'react'
import { formatDateLong, formatMoney, printOrderUrl, viewOrderUrl, getGarmentByAbbreviation } from '@trinity/utils'
import { Link } from 'react-router'
import {
  Box,
  Button,
  Divider,
  IconButton,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Stack,
  StatusChip,
  Typography,
  type BoxProps,
} from '../../components'
import { ImageNotFound } from '../../assets'
import { GarmentDrawer } from './GarmentDrawer'
import { useGlobalState } from '../../hooks'

interface OrderDetailsProps {
  order: TrinityAPI.OrderExtendedType
  garments: TrinityAPI.GarmentExtendedType[]
}

export function OrderDetails({ order, garments }: OrderDetailsProps) {
  return (
    <Stack p={{ mobile: 2, tablet: 4 }} spacing={3}>
      <Stack direction='row' alignItems='center' spacing={1}>
        <IconButton component={Link} to='/'>
          <KeyboardArrowLeft />
        </IconButton>

        <Typography variant='body3' color='text.secondary'>
          Orders / {order.id}
        </Typography>
      </Stack>

      <Typography variant='h1'>Order Details</Typography>

      <OrderDetailsHeader order={order} />

      <Stack direction={{ mobile: 'column', laptop: 'row' }} spacing={2} alignItems='stretch'>
        <ShippingDetailsCard order={order} sx={{ flex: 1, minWidth: 0 }} />
        <FinancialSummaryCard order={order} sx={{ flex: 1, minWidth: 0 }} />
      </Stack>

      <Divider />

      <GarmentsList garments={garments} order={order} />
    </Stack>
  )
}

function OrderDetailsHeader({ order }: { order: TrinityAPI.OrderExtendedType }) {
  const { onSkiffle } = useGlobalState()
  return (
    <Stack
      sx={{
        borderRadius: theme => theme.shape.borderRadius,
        border: 'n100',
        p: { mobile: 2, tablet: 3 },
      }}
      spacing={2}
    >
      <Stack spacing={1}>
        <Link to={`/clients/${order.customer.id}`}>
          <Typography variant='h2'>{order.customer.name}</Typography>
        </Link>
        <Stack direction={{ mobile: 'column', tablet: 'row' }} spacing={2}>
          <MetaItem label='Order #' value={order.title} />
          <MetaItem label='Dealer' value={order.dealer.name} />
          <MetaItem label='Date' value={formatDateLong(order.createdAt)} />
        </Stack>
      </Stack>

      {!onSkiffle && (
        <>
          {order.customOrderNumber?.trim() && (
            <>
              <Divider sx={{ my: 2 }} />
              <Stack direction={{ mobile: 'column', tablet: 'row' }} spacing={2}>
                <MetaItem label='Order Description' value={order.customOrderNumber.trim()} />
              </Stack>
            </>
          )}

          <Divider sx={{ my: 2 }} />

          <Stack direction='row' spacing={1.5}>
            <Button variant='outlined' href={printOrderUrl(order.id)} target='_blank' rel='noopener noreferrer'>
              Print Order
            </Button>
            <Button variant='outlined' href={viewOrderUrl(order.id)} target='_blank' rel='noopener noreferrer'>
              View Order in Workflow
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  )
}

function ShippingDetailsCard({ order, sx = {} }: { order: TrinityAPI.OrderExtendedType; sx?: BoxProps['sx'] }) {
  const address = order.shippingAddress
  return (
    <Stack
      sx={{
        borderRadius: theme => theme.shape.borderRadius,
        border: 'n100',
        p: { mobile: 2, tablet: 3 },
        ...sx,
      }}
    >
      <Typography variant='h6'>Shipping Details</Typography>

      <Divider sx={{ my: 2 }} />

      <Stack spacing={2}>
        <Stack spacing={0.25}>
          <Typography variant='caption' color='text.secondary'>
            Final Destination
          </Typography>
          {address.contactName && <Typography variant='body3'>{address.contactName}</Typography>}
          {address.street1 && <Typography variant='body3'>{address.street1}</Typography>}
          {address.street2 && <Typography variant='body3'>{address.street2}</Typography>}
          <Typography variant='body3'>
            {address?.city ?? 'N/A'}, {address?.state ?? 'N/A'} {address?.zip ?? 'N/A'}
          </Typography>
        </Stack>
        <Stack spacing={0.25}>
          <Typography variant='caption' color='text.secondary'>
            Shipping Method
          </Typography>
          <Typography variant='body3'>{order.shipType ?? 'Ground'}</Typography>
        </Stack>
      </Stack>
    </Stack>
  )
}

function FinancialSummaryCard({ order, sx = {} }: { order: TrinityAPI.OrderExtendedType; sx?: BoxProps['sx'] }) {
  const { user } = useGlobalState()

  if (!user.permissions.nav.includes('can_view_price_list')) return null

  return (
    <Stack
      sx={{
        borderRadius: theme => theme.shape.borderRadius,
        border: 'n100',
        p: { mobile: 2, tablet: 3 },
        ...sx,
      }}
    >
      <Typography variant='h6'>Invoice Details</Typography>

      <Divider sx={{ my: 2 }} />

      <KeyValue label='Order Subtotal' value={formatMoney(order.currency.name, Number(order.subtotal))} />
      {Number(order.totalDiscount) > 0 && (
        <KeyValue label='Discount' value={formatMoney(order.currency.name, -Number(order.totalDiscount))} />
      )}
      <KeyValue label='Shipping' value={formatMoney(order.currency.name, Number(order.shipCost))} />
      <KeyValue label='Tariff Surcharge' value={formatMoney(order.currency.name, Number(order.tariffCost))} />
      <KeyValue label='Tax' value={formatMoney(order.currency.name, Number(order.tax))} />

      <Divider sx={{ my: 1.5 }} />

      <KeyValue label='Order Total' value={formatMoney(order.currency.name, Number(order.grandTotal))} bold />
      <KeyValue label='Current Balance' value={formatMoney(order.currency.name, Number(order.currentBalance))} />
    </Stack>
  )
}

function GarmentsList({
  garments,
  order,
}: {
  garments: TrinityAPI.GarmentExtendedType[]
  order: TrinityAPI.OrderExtendedType
}) {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [activeGarment, setActiveGarment] = useState<TrinityAPI.GarmentExtendedType | null>(null)

  const openDrawer = (garment: TrinityAPI.GarmentExtendedType) => {
    setActiveGarment(garment)
    setDrawerOpen(true)
  }
  return (
    <Stack spacing={2}>
      <Stack
        direction={{ mobile: 'column', tablet: 'row' }}
        justifyContent='space-between'
        alignItems={{ mobile: 'flex-start', tablet: 'center' }}
      >
        <Typography variant='h3'>Garments</Typography>
        <Typography variant='body3' color='text.secondary'>
          {garments.length} garments
        </Typography>
      </Stack>

      <Stack spacing={2}>
        {garments.map(garment => (
          <GarmentCard
            key={`garment-card-${garment.id}`}
            garment={garment}
            order={order}
            onClick={() => openDrawer(garment)}
          />
        ))}
      </Stack>

      {activeGarment && (
        <GarmentDrawer garment={activeGarment} order={order} open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      )}
    </Stack>
  )
}

function GarmentCard({
  garment,
  order,
  onClick,
}: {
  garment: TrinityAPI.GarmentExtendedType
  order: TrinityAPI.OrderExtendedType
  onClick: () => void
}) {
  const { user } = useGlobalState()

  const canViewPrices = user.permissions.nav.includes('can_view_price_list')
  const currency = order.currency.name

  return (
    <Stack
      direction='row'
      spacing={2}
      alignItems='center'
      height={200}
      sx={{
        borderRadius: theme => theme.shape.borderRadius,
        border: 'n100',
        px: { mobile: 2, tablet: 3 },
        cursor: 'pointer',
        '&:hover': { bgcolor: 'grey.50' },
      }}
      onClick={onClick}
    >
      <img
        width={100}
        src={
          garment?.renderedUri?.replace('width=500', 'width=1000') ??
          garment?.fabric?.url ??
          garment?.fabric?.swatchUrl ??
          ImageNotFound
        }
        alt={garment.title}
        style={{ objectFit: 'cover' }}
      />
      <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant='h6'>{garment.title}</Typography>
        <Typography variant='caption' sx={{ letterSpacing: 0.6, color: 'text.secondary' }}>
          {getGarmentByAbbreviation(garment.garmentType)?.formalName}
        </Typography>
      </Stack>

      <Stack alignItems='flex-end' spacing={2} sx={{ display: { mobile: 'none', tablet: 'flex' } }}>
        {canViewPrices && (
          <Typography variant='h4'>
            {formatMoney(currency, (Number(garment?.price ?? 0) + Number(garment?.optionCost ?? 0)).toString())}
          </Typography>
        )}
        <StatusChip status={garment.orderStatus.description} />
      </Stack>

      <Box sx={{ display: { mobile: 'flex' }, alignItems: 'center', ml: 'auto' }}>
        <KeyboardArrowRight sx={{ color: 'text.secondary' }} />
      </Box>
    </Stack>
  )
}

//* ============ PAGE HELPER COMPONENTS ============ *//

function KeyValue({ label, value, bold = false }: { label: string; value: string; bold?: boolean }) {
  return (
    <Stack direction='row' justifyContent='space-between'>
      <Typography variant='body3' color='text.secondary'>
        {label}
      </Typography>
      <Typography variant='body3' sx={{ fontWeight: bold ? 700 : 400 }}>
        {value}
      </Typography>
    </Stack>
  )
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction={{ mobile: 'column', tablet: 'row' }} spacing={0.5}>
      <Typography variant='body3' color='text.secondary'>
        {label}:
      </Typography>
      <Typography variant='body3'>{value}</Typography>
    </Stack>
  )
}
